import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { selectUseId } from '@/features/auth/auth.slice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';

import useComponentVisible from '../../../../../hooks/useComponentVisible';
import { createRandomString, isAlphabetic } from '../../../../tools';
import AddNoteButton from '../../AddNoteButton';
import DeleteButton from '../../DeleteButton';
import NodeParentChanger from '../../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import { MacBotButtonSettingForm } from './scenario-button-setting-form.component';
import ScenarioSide from './scenario-side';
import { ScenarioSimpleTextInput } from './scenario-simple-text-input';
import { TextInputWithHint } from './TextInputWithHint';

export const MacBotNotifyMeByEmailForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  config,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
}: {
  setShow?: (item: boolean) => void;
  itemData: any;
  parentId: string | null;
  tree: any[];
  config: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
}) => {
  const { t } = useTranslation();
  const [showButtonSetting, setShowButtonSetting] = useState(false);
  const [selectedButtonToEdit, setSelectedButtonToEdit] = useState<number>(-1);
  const [quickReplies, setQuickReplies] = useState<any[]>([]);
  const [showDeleteButton, setShowDeleteButton] = useState(true);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    top: 0,
    left: 0,
    currentCaretPosition: 0,
  });

  const { ref, isComponentVisible } = useComponentVisible(showContextMenu);
  const [cloneJson, setCloneJson] = useState<IResponseForm | null>(null);
  const userId = useAppSelector(selectUseId);

  interface IResponseForm extends INoteForm {
    BlockTitle?: string;
    BotResponse: string;
  }

  const schema = Yup.object({
    BlockTitle: Yup.string().required(t('MacForm/l-01')).max(30, t('Wizard/validationLabels/key16')),
    BotResponse: Yup.string().required(t('MacForm/l-02')),
    ...getNoteSchema(t),
  });

  const {
    register,
    formState: { errors },
    reset,
    watch,
    trigger,
    getValues,
    setValue,
    handleSubmit,
    control,
  } = useForm<IResponseForm>({
    resolver: yupResolver(schema) as any,
    mode: 'onSubmit',
    defaultValues: {
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
  });
  const { fields } = useFieldArray({
    control,
    name: 'notes',
  });

  //Check Show Button Delete
  useEffect(() => {
    if (itemData) {
      const find = tree?.find(dr => dr.id == itemData.id);
      if (find) {
        if (find.parent) {
          setShowDeleteButton(true);
        } else {
          setShowDeleteButton(false);
        }
      }
    }
  }, [tree, itemData]);

  const [contextMenuList, setContextMenuList] = useState<any[]>([]);
  const popupAudioPath: { [key: string]: string } = {
    en: 'https://cdn.1cdn.app/application/LOUISEBOT/24092716242734_add_bot_message_en.mp3',
    fr: 'https://cdn.1cdn.app/application/LOUISEBOT/24092716242877_add_bot_message_fr.mp3',
  };

  useEffect(() => {
    reset({
      BlockTitle: itemData?.title ?? '',
      BotResponse: itemData?.message ?? '',
      notes: itemData?.notes,
    });

    setCloneJson({
      BlockTitle: itemData?.title ?? '',
      BotResponse: itemData?.message ?? '',
      showNoteForm: itemData?.notes?.length > 0 || false,
      notes: itemData?.notes,
    });

    if (!itemData) {
      setShowDeleteButton(false);
    }
  }, [itemData]);

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
  };

  const removeButtonHandler = (btnIndex: number) => {
    setQuickReplies(prev => prev.filter((p, index) => index !== btnIndex));
  };
  const addButtonHandler = (qBtn: number) => {
    setQuickReplies(prev => [...prev, qBtn]);
  };

  const editButtonHandler = (_index: number, qRep: any) => {
    setQuickReplies(prev => prev.map((p, index) => (index === _index ? qRep : p)));

    setSelectedButtonToEdit(-1);
  };

  const handleQuickReplySelect = (index: number) => {
    setSelectedButtonToEdit(index);
    setShowButtonSetting(true);
  };

  const closeButtonSettingModal = () => {
    setShowButtonSetting(false);
    setSelectedButtonToEdit(-1);
  };

  const createTitle = (text: string) => {
    let titleCode = 0;
    tree.forEach(element => {
      if (element.type === BlockType.Response) {
        const _code = element.title.split('-');
        if (_code[0] && Number(_code[0]) > titleCode) titleCode = +_code[0];
      }
    });

    if (typeof text != 'string' || !text) {
      return '';
    }
    let acronym = text
      ?.match(/[\p{Alpha}\p{Nd}]+/gu)
      ?.reduce((previous, next) => previous + (+next === 0 || parseInt(next) ? parseInt(next) : next[0] || ''), '')
      ?.toUpperCase();

    if (!acronym || (acronym && acronym.length < 3) || !isAlphabetic(acronym)) {
      acronym = createRandomString(5);
    }

    return `${titleCode + 1}-${acronym?.slice(0, 3)}`;
  };

  const ButtonSubmit = (data: IResponseForm) => {
    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        type: BlockType.NotifyMeByEmail,
        title: data.BlockTitle,
        message: data.BotResponse,
        parent: itemData.parent,
        notes: data?.notes,
      };

      if (
        JSON.stringify(cloneJson) ==
        JSON.stringify({
          BlockTitle: data.BlockTitle,
          BotResponse: data.BotResponse,
          notes: data?.notes,
          showNoteForm: data.showNoteForm,
        })
      ) {
        setShow?.(false);
        return;
      }

      changeScenarioBlock(_data).then(() => {
        reset();
        setShow?.(false);
      });
    } else {
      const _data = {
        type: BlockType.NotifyMeByEmail,
        title: data.BlockTitle,
        message: data.BotResponse,
        parent: parentId,
        notes: data?.notes,
      };
      createScenarioBlock(_data).then(() => {
        reset();
        setShow?.(false);
      });
    }
  };

  const handleRightClick = (event: any) => {
    event.preventDefault();
    setShowContextMenu(true);
    const rect = event.currentTarget.getBoundingClientRect();
    const currentCaretPosition = event.currentTarget.selectionStart;

    setContextMenuPosition({
      top: event.clientY - rect.top,
      left: event.clientX - rect.left,
      currentCaretPosition: currentCaretPosition,
    });
  };

  // Function to measure caret's pixel position using a canvas approach
  const measureCaret = (input: any, caretIndex: any) => {
    const rect = input.getBoundingClientRect();
    return { top: rect.top, left: rect.left };
  };

  // Function to get the caret position from the event

  const getCaretPosition = (e: any) => {
    const inputElement = e.target;
    const caretIndex = inputElement.selectionStart;

    // Measure the caret's pixel position
    const { top, left } = measureCaret(inputElement, caretIndex);

    // Set the caret position for the popup
    setContextMenuPosition({
      top: e.clientY - top,
      left: e.clientX - left,
      currentCaretPosition: caretIndex,
    });

    const value: string = e.target.value;

    if (value.endsWith('$:var_') || value.endsWith('$:var_ ') || value.endsWith('/')) {
      setShowContextMenu(true);
    }
  };

  useEffect(() => {
    setShowContextMenu(false);
  }, [isComponentVisible]);

  const onSelectVariable = (node: any) => {
    let value = watch('BotResponse');

    if (node) {
      value = value + ' ';
      value = value.replaceAll('/ ', '');
      value = value.replaceAll('$:var_ ', '');

      const updatedValue =
        value.slice(0, contextMenuPosition.currentCaretPosition) + ` ${node.variableName} ` + value.slice(contextMenuPosition.currentCaretPosition);

      setValue('BotResponse', updatedValue);
      setShowContextMenu(false);
    }
  };

  useEffect(() => {
    if (config?.metaData?.variables) {
      setContextMenuList([...config.metaData.variables]);
    }
  }, [config]);

  return (
    <React.Fragment>
      <ScenarioSide size="md" onSubmit={handleSubmit(ButtonSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowNotifyMeByEmail}
          onClose={() => {
            reset();
          }}
        />

        <div className="tw-w-full tw-h-full tw-px-3 tw-py-2 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-inline-flex tw-font-[Lexend]">
          <TextInputWithHint
            label={t('MacBotResponseForm/key1')}
            name="BlockTitle"
            register={register('BlockTitle')}
            error={errors.BlockTitle}
            max={30}
            maxLength={30}
            HintMessage={['MacBotResponseForm/maxlenght']}
          />

          <Form.Group className="tw-w-full ">
            <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{t('MacBotResponseForm/key2')}</Form.Label>

            <ScenarioSimpleTextInput
              isMultiline={true}
              placeholder={t('MacNotifyEmail/l-04')}
              setValue={text => {
                setValue('BotResponse', text);
              }}
              value={getValues('BotResponse')}
              contextMenuList={contextMenuList}
              isInvalid={!!errors.BotResponse}
            />
            <Form.Control.Feedback type="invalid" className="d-block font-poppins">
              {errors.BotResponse?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            {showDeleteButton && <DeleteButton deleteHandler={deleteHandler} />}

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton id="btn-notify-submit" type="submit" disabled={loading} isLoading={loading} title={t('MacBotResponseForm/btn-02')} />
          </div>
        </div>
        <NoteForm noteFormMethods={{ register, watch, errors, fields, setValue }} />
      </ScenarioSide>

      <MacBotButtonSettingForm
        show={showButtonSetting}
        closeModal={closeButtonSettingModal}
        addQuickReplies={addButtonHandler}
        editQuickReplies={editButtonHandler}
        selectedButtonToEdit={selectedButtonToEdit}
        quickReplies={quickReplies}
        removeButton={removeButtonHandler}
        tree={tree}
      />
    </React.Fragment>
  );
};
